import React, { useMemo } from 'react';

/**
 * StarField — Pure CSS twinkling stars with optional moon + shooting stars.
 * Place inside a relatively-positioned container.
 *
 * Props:
 *   count        — number of stars (default 220)
 *   showMoon     — render the moon glow (default true)
 *   shootingStars — render shooting stars (default true)
 *   className    — extra classes
 */
export const StarField = ({
  count = 220,
  showMoon = true,
  shootingStars = true,
  className = '',
}) => {
  // Memoise so stars don't re-randomise on every re-render
  const stars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < count; i++) {
      const r = Math.random();
      const size = r > 0.92 ? 'lg' : r > 0.65 ? 'md' : 'sm';
      const isGold = Math.random() > 0.94;
      arr.push({
        id: i,
        size,
        gold: isGold,
        top: Math.random() * 100,
        left: Math.random() * 100,
        delay: Math.random() * 6,
        duration: 3 + Math.random() * 5, // 3–8s
      });
    }
    return arr;
  }, [count]);

  return (
    <div className={`starfield ${className}`} aria-hidden="true">
      {stars.map((s) => (
        <span
          key={s.id}
          className={`star star-${s.size} ${s.gold ? 'star-gold' : ''}`}
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
          }}
        />
      ))}
      {shootingStars && (
        <>
          <span className="shooting-star" />
          <span className="shooting-star delay-2" />
        </>
      )}
      {showMoon && <div className="moon-glow" />}
    </div>
  );
};

export default StarField;
